import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import {
  getuserById,
  updateUserCredential,
} from "../../helpers/backend_helper";
import { postData } from "../../helpers/url-request";
import { showLoading } from "react-global-loading";
import "../Standards/Standards.scss";

const UserPassword = ({ userId, closeAction, external = false }) => {
  const [user, setUser] = useState({ fullName: "", loginName: "" });

  useEffect(() => {
    if (!userId) return;
    showLoading(true);
    getuserById(userId)
      .then((response) => {
        if (response.status)
          setUser({
            fullName: response.data.fullName || "",
            loginName:
              response.data.loginName ||
              response.data.userLogin?.loginName ||
              "",
          });
        else ErrorNotify(response.message);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load the user details.");
      })
      .finally(() => showLoading(false));
  }, [userId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { newPassword: "", confPassword: "" },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(
          external ? 8 : 6,
          `Password must be at least ${external ? 8 : 6} characters`
        )
        .max(256, "Password must be 256 characters or fewer")
        .matches(external ? /[a-z]/ : /./, "Include a lowercase letter")
        .matches(external ? /[A-Z]/ : /./, "Include an uppercase letter")
        .matches(external ? /[0-9]/ : /./, "Include a number")
        .matches(external ? /[^A-Za-z0-9]/ : /./, "Include a symbol")
        .required("Enter a password"),
      confPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Confirm the password"),
    }),
    onSubmit: (values) => {
      showLoading(true);
      const update = external
        ? postData(`/user/resetexternalpassword/${userId}`, {
            newPassword: values.newPassword,
            confirmPassword: values.confPassword,
          })
        : updateUserCredential({
            userId,
            newPassword: values.newPassword,
            confPassword: values.confPassword,
          });
      update
        .then((response) => {
          if (response.status) {
            SuccessNotify(
              external
                ? "Entra password set. The user can sign in with this password."
                : "Password updated successfully."
            );
            validation.resetForm();
            closeAction?.();
          } else ErrorNotify(response.message);
        })
        .catch((error) => {
          ErrorNotify("Unable to update the password.");
        })
        .finally(() => showLoading(false));
    },
  });

  return (
    <Form
      className="standard-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        validation.handleSubmit();
      }}
    >
      <div className="standard-drawer-form__content">
        <p className="standard-drawer-form__intro">
          {external
            ? "Set a password for this Entra identity. It is sent directly to Microsoft and can be used at the next sign-in without a required password change."
            : "Set a new password for this user. They can use it to sign in immediately."}
        </p>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-user" />
              User
            </span>
          </div>
          <FormGroup>
            <Label>Full Name</Label>
            <Input value={user.fullName} disabled />
          </FormGroup>
          <FormGroup>
            <Label>Login Name</Label>
            <Input value={user.loginName} disabled />
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-lock-alt" />
              {external ? "Entra Password" : "New Password"}
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="reset-password">
              Password <b>*</b>
            </Label>
            <Input
              id="reset-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              placeholder="Enter new password"
              value={validation.values.newPassword}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.newPassword && validation.errors.newPassword
              )}
            />
            {validation.touched.newPassword &&
              validation.errors.newPassword && (
                <FormFeedback>{validation.errors.newPassword}</FormFeedback>
              )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="reset-confirm-password">
              Confirm Password <b>*</b>
            </Label>
            <Input
              id="reset-confirm-password"
              name="confPassword"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={validation.values.confPassword}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.confPassword &&
                  validation.errors.confPassword
              )}
            />
            {validation.touched.confPassword &&
              validation.errors.confPassword && (
                <FormFeedback>{validation.errors.confPassword}</FormFeedback>
              )}
          </FormGroup>
        </section>
      </div>
      <footer className="standard-drawer-form__actions">
        <Button
          className="standard-drawer-form__cancel"
          type="button"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <Button className="standard-drawer-form__submit" type="submit">
          {external ? "Set Entra Password" : "Reset Password"}
        </Button>
      </footer>
    </Form>
  );
};

export default UserPassword;
